// src/components/RhinoVOIPRecentCalls.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Button,
  Dropdown,
  DropdownMenuItem,
  DropdownMenuItemWild,
  Icon,
  RhinoSwitch,
  UtilityInlineGrid,
} from 'rhinostyle';
import { useGetRecentCallsQuery } from '../services/VoipService';
import useIsMobile from '../hooks/useIsMobile';
import '../assets/styles/rhinovoip-recent-calls.less';
import { useVoip } from '../voip/VoipProvider';
import incomingCallIcon from '../assets/images/voip/incoming-call-icon.svg';
import outgoingCallIcon from '../assets/images/voip/outgoing-call-icon.svg';
import ringGroupIcon from '../assets/images/voip/ring-group-icon.svg';
import contactCardIcon from '../assets/images/voip/contact-card-icon.svg';
import makeCallIcon from '../assets/images/voip/make-call-icon.png';
import voicemailIcon from '../assets/images/voip/voicemail-icon.png';
import ContactSidePanel from './ContactSidePanel';
import EmptyMessage from './EmptyMessage';
import { formatStatus, formatDuration, formatPhone } from '../helpers/VoipHelpers';

// ------------------ Constants ------------------

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const DIRECTION_OPTIONS = [
  { id: 'inbound', value: 'Inbound' },
  { id: 'outbound', value: 'Outbound' },
  { id: 'local', value: 'Internal' },
];

const STATUS_OPTIONS = [
  { id: 'answered', value: 'Answered' },
  { id: 'missed', value: 'Missed' },
  { id: 'no_answer', value: 'No Answer' },
  { id: 'voicemail', value: 'Voicemail' },
  { id: 'busy', value: 'Busy' },
  { id: 'failed', value: 'Failed' },
];

// ------------------ Component ------------------

const RhinoVOIPRecentCalls = ({ onNavigateToVoicemail }) => {
  const { myExtension, call: placeCall, callPhase } = useVoip();
  const [includeRinggroups, setIncludeRinggroups] = useState(true);
  const [sidePanelPhone, setSidePanelPhone] = useState(null);
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetRecentCallsQuery(includeRinggroups);

  useEffect(() => {
    let t;

    if (callPhase === 'ended') {
      t = setTimeout(() => {
        refetch();
      }, 500);
    }

    return () => {
      if (t) clearTimeout(t);
    };
  }, [callPhase, refetch]);

  // Match the Voicemail tab's refresh cadence so a freshly-uploaded voicemail
  // shows up in Recents at roughly the same time it shows up in the Voicemail tab.
  useEffect(() => {
    refetch();
    const intervalId = setInterval(() => {
      refetch();
    }, 10_000);
    return () => clearInterval(intervalId);
  }, [refetch]);

  const [confirmCall, setConfirmCall] = useState(null);
  const isMobile = useIsMobile(768);
  const VOICEMAIL_LABEL = 'My Voicemail';

  // ------------------ Filters ------------------

  const [selectedDirections, setSelectedDirections] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedRingGroups, setSelectedRingGroups] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [displayPageSize, setDisplayPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [closeCounters, setCloseCounters] = useState({ direction: 0, status: 0, member: 0, contact: 0, ringGroup: 0 });
  const [memberSearch, setMemberSearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [ringGroupSearch, setRingGroupSearch] = useState('');

  const handleDropdownOpen = (name) => {
    setCloseCounters((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k !== name) next[k] += 1; });
      return next;
    });
  };

  const toggleDirection = (value) => {
    setSelectedDirections((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleStatus = (value) => {
    setSelectedStatuses((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleMember = (value) => {
    setSelectedMembers((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleContact = (value) => {
    setSelectedContacts((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleRingGroup = (value) => {
    setSelectedRingGroups((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const clearAllFilters = useCallback(() => {
    setSelectedDirections([]);
    setSelectedStatuses([]);
    setSelectedMembers([]);
    setSelectedContacts([]);
    setSelectedRingGroups([]);
    setMemberSearch('');
    setContactSearch('');
    setRingGroupSearch('');
    setPageNumber(0);
    setFilterResetKey((prev) => prev + 1);
  }, []);

  // ------------------ Derived data ------------------

  const allCalls = Array.isArray(data?.objects) ? data.objects : [];

  const STAR_CODE_LABELS = { '*97': 'Voicemail', '*411': 'Company Directory' };
  const isStarCode = (num) => num && (STAR_CODE_LABELS[num] || (num.startsWith('*') && !num.startsWith('*99')));

  const getMemberKeys = (r) => {
    const keys = [];
    if (r.caller_extension) {
      keys.push(String(r.caller_extension));
    }
    if (r.dest_extension) {
      keys.push(String(r.dest_extension));
    }
    if (STAR_CODE_LABELS[r.destination_number]) {
      keys.push(`star:${r.destination_number}`);
    }
    return keys;
  };

  const getContactKeys = (r) => {
    const keys = [];
    if (!r.caller_extension && r.caller_id_number) {
      keys.push(r.caller_contact_name || r.caller_id_number);
    }
    if (!r.dest_extension && !r.ring_group_name && !isStarCode(r.destination_number) && r.destination_number && !r.destination_number.startsWith('*99')) {
      keys.push(r.destination_contact_name || r.destination_number);
    }
    return keys;
  };

  const memberOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allCalls.forEach((r) => {
      if (r.caller_extension) {
        const key = String(r.caller_extension);
        if (!seen.has(key)) {
          seen.add(key);
          const name = r.caller_extension_name || r.caller_id_name || key;
          acc.push({ id: key, value: `${name} (Ext. ${key})` });
        }
      }
      if (r.dest_extension) {
        const key = String(r.dest_extension);
        if (!seen.has(key)) {
          seen.add(key);
          const name = r.dest_name || key;
          acc.push({ id: key, value: `${name} (Ext. ${key})` });
        }
      }
      const starLabel = STAR_CODE_LABELS[r.destination_number];
      if (starLabel) {
        const key = `star:${r.destination_number}`;
        if (!seen.has(key)) {
          seen.add(key);
          acc.push({ id: key, value: starLabel });
        }
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allCalls]);

  const contactOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allCalls.forEach((r) => {
      if (!r.caller_extension && r.caller_id_number) {
        const key = r.caller_contact_name || r.caller_id_number;
        if (!seen.has(key)) {
          seen.add(key);
          const label = r.caller_contact_name
            ? `${r.caller_contact_name} - ${formatPhone(r.caller_id_number)}`
            : formatPhone(r.caller_id_number);
          acc.push({ id: key, value: label });
        }
      }
      if (!r.dest_extension && !r.ring_group_name && !isStarCode(r.destination_number) && r.destination_number && !r.destination_number.startsWith('*99')) {
        const key = r.destination_contact_name || r.destination_number;
        if (!seen.has(key)) {
          seen.add(key);
          const label = r.destination_contact_name
            ? `${r.destination_contact_name} - ${formatPhone(r.destination_number)}`
            : formatPhone(r.destination_number);
          acc.push({ id: key, value: label });
        }
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allCalls]);

  const ringGroupOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allCalls.forEach((r) => {
      if (r.ring_group_name && !seen.has(r.ring_group_name)) {
        seen.add(r.ring_group_name);
        acc.push({ id: r.ring_group_name, value: r.ring_group_extension ? `${r.ring_group_name} (${r.ring_group_extension})` : r.ring_group_name });
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allCalls]);

  const filteredCalls = useMemo(() => {
    let filtered = allCalls;
    if (!includeRinggroups) {
      filtered = filtered.filter((call) => !call.ring_group_name);
    }
    if (selectedDirections.length > 0) {
      filtered = filtered.filter((call) => selectedDirections.includes(call.direction));
    }
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((call) => selectedStatuses.includes(call.status?.toLowerCase()));
    }
    if (selectedMembers.length > 0) {
      filtered = filtered.filter((r) => getMemberKeys(r).some((k) => selectedMembers.includes(k)));
    }
    if (selectedContacts.length > 0) {
      filtered = filtered.filter((r) => getContactKeys(r).some((k) => selectedContacts.includes(k)));
    }
    if (selectedRingGroups.length > 0) {
      filtered = filtered.filter((r) => r.ring_group_name && selectedRingGroups.includes(r.ring_group_name));
    }
    return filtered;
  }, [allCalls, includeRinggroups, selectedDirections, selectedStatuses, selectedMembers, selectedContacts, selectedRingGroups]);

  const totalFilteredCount = filteredCalls.length;
  const totalPages = Math.ceil(totalFilteredCount / displayPageSize);
  const paginatedCalls = filteredCalls.slice(pageNumber * displayPageSize, (pageNumber + 1) * displayPageSize);

  // ------------------ Pagination ------------------

  const handlePagination = (direction) => {
    if (direction === 'next') {
      setPageNumber((prev) => prev + 1);
    } else if (direction === 'previous' && pageNumber > 0) {
      setPageNumber((prev) => prev - 1);
    }
  };

  const isPreviousDisabled = pageNumber === 0;
  const isNextDisabled = pageNumber >= totalPages - 1;
  const pageItemIndexFrom = totalFilteredCount > 0 ? pageNumber * displayPageSize + 1 : 0;
  const pageItemIndexTo = Math.min((pageNumber + 1) * displayPageSize, totalFilteredCount);

  const handlePageSizeChange = (e) => {
    setDisplayPageSize(Number(e.target.value));
    setPageNumber(0);
  };

  // ------------------ Filter state checks ------------------

  const isDirectionSelected = selectedDirections.length > 0;
  const isStatusSelected = selectedStatuses.length > 0;
  const isMemberSelected = selectedMembers.length > 0;
  const isContactSelected = selectedContacts.length > 0;
  const isRingGroupSelected = selectedRingGroups.length > 0;
  const isAnyFilterSelected = isDirectionSelected || isStatusSelected || isMemberSelected || isContactSelected || isRingGroupSelected;

  const directionLabel = isDirectionSelected ? `${selectedDirections.length} Directions Selected` : 'Direction';
  const statusLabel = isStatusSelected ? `${selectedStatuses.length} Statuses Selected` : 'Status';
  const memberLabel = isMemberSelected ? `${selectedMembers.length} Members Selected` : 'Member';
  const contactLabel = isContactSelected ? `${selectedContacts.length} Contacts Selected` : 'Contact';
  const ringGroupLabel = isRingGroupSelected ? `${selectedRingGroups.length} Ring Groups Selected` : 'Ring Group';

  // ------------------ Helpers ------------------

  if (isLoading) return <div>Loading call logs...</div>;
  if (error && allCalls.length === 0) {
    return <div className="cdr-empty-state">Unable to load recent calls. Please try again.</div>;
  }

  function extractCallTarget(call) {
    const type = classifyDirection(call);

    if (type === 'Outgoing') {
      const dest = call.destination_number;
      // *99XX is PBX voicemail routing — use the actual dialed number
      if (dest && dest.startsWith('*99')) {
        return call.ring_group_extension || call.caller_destination || dest.replace('*99', '');
      }
      return dest;
    }

    return call.caller_id_number;
  }

  function openCallConfirm(call) {
    const target = extractCallTarget(call, myExtension);

    let label = '';

    if (target === '*97') {
      label = VOICEMAIL_LABEL;
    } else {
      const type = classifyDirection(call);
      label = (type === 'Outgoing' || type === 'Outbound External')
        ? (call.destination_contact_name || call.dest_name || call.ring_group_name || target)
        : (call.caller_contact_name || call.caller_id_name || target);
    }

    setConfirmCall({ targetNumber: target, label });
  }

  function handleMobileClick(call) {
    const target = extractCallTarget(call);
    if (!target) return;

    try {
      placeCall(target);
    } catch (err) {
      console.error('Mobile call start failed:', err);
    }
  }

  function formatDate(iso) {
    if (!iso) return '-';

    const d = new Date(iso);
    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
    })}`;
  }

  function displayWithRingGroupIcon(label, number = null, showIcon = true) {
    const text = number ? `${label} (${number})` : label;

    if (!showIcon) return text;

    return (
      <span className="ringgroup-label">
        <img src={ringGroupIcon} alt="Ring Group" className="ringgroup-icon" /> {text}
      </span>
    );
  }

  // ------------------ Direction Classification ------------------

  function classifyDirection(call) {
    const dest = call.destination_number;
    const cid = call.caller_id_number;
    const dir = call.direction;

    // --- Voicemail Dial (OUTGOING) ---
    // If I called *97 → Outgoing
    if (cid === myExtension && dest === '*97') return 'Outgoing';

    // --- External outbound ---
    if (dir === 'outbound') return 'Outgoing';

    // --- External inbound ---
    if (dir === 'inbound') return 'Incoming';

    // --- Internal calls ----
    if (dir === 'local') {
      if (cid === myExtension) return 'Outgoing';
      return 'Incoming';
    }

    return dir;
  }

  // ------------------ UI Icons ------------------
  function directionIcon(type) {
    switch (type) {
      case 'Outgoing':
      case 'Outbound External':
        return outgoingCallIcon;
      case 'Incoming':
      case 'Inbound External':
        return incomingCallIcon;
      default:
        return '';
    }
  }

  // ------------------ Caller / Receiver Logic ------------------

  function callFrom(call) {
    const num = call.caller_id_number;
    const contactName = call.caller_contact_name;
    const displayName = contactName || call.caller_id_name;
    const type = classifyDirection(call);
    // BUG-025: For inbound calls whose caller_id_number is an external phone number, never fall
    // back to caller_extension (which may have been populated from IVR routing to a direct ext).
    const numDigits = (num || '').replace(/\D/g, '');
    const looksExternal = numDigits.length >= 10;
    const isInbound = type === 'Incoming' || type === 'Inbound External';
    const callerExt = isInbound && looksExternal ? null : call.caller_extension;

    // Me
    if (num === myExtension) return `Me (Ext. ${num})`;

    // Named caller with extension
    if (displayName && displayName !== num) {
      if (callerExt) return `${displayName} (Ext. ${callerExt})`;
      return `${displayName} - ${formatPhone(num)}`;
    }
    if (callerExt) return `Ext. ${callerExt}`;
    return formatPhone(num) || 'Unknown';
  }

  function callTo(call) {
    const dest = call.destination_number;
    const contactName = call.destination_contact_name;
    const destName = contactName || call.dest_name;
    const destExt = call.dest_extension;
    const rgName = call.ring_group_name;
    const type = classifyDirection(call);
    // *99XX is PBX voicemail routing — use caller_destination (the number actually dialed) or the ring group extension
    const displayDest = (dest && dest.startsWith('*99'))
      ? (call.ring_group_extension || call.caller_destination || destExt || dest.replace('*99', ''))
      : dest;

    // Voicemail
    if (dest === '*97') return `${VOICEMAIL_LABEL} (${dest})`;

    // To Me
    if (displayDest === myExtension) return `Me (Ext. ${displayDest})`;

    // BUG-021: Ring group call that was answered — surface the answerer's name in the Contact column
    // (cdr_service.py swaps caller_extension → dest_extension for inbound RG calls).
    if (rgName && (type === 'Incoming' || type === 'Inbound External') && destName) {
      if (destExt) return `${destName} (Ext. ${destExt})`;
      return destName;
    }

    // Ring Group (unanswered or outbound)
    if (rgName) return `${rgName} (${displayDest})`;

    // Named internal
    if (destName && destName !== displayDest) {
      if (type !== 'Outbound External') {
        if (destExt) return `${destName} (Ext. ${destExt})`;
        return `${destName} - ${formatPhone(displayDest)}`;
      }
    }

    // Fallback
    if (destExt) return `Ext. ${destExt}`;
    return formatPhone(displayDest) || '-';
  }

  function displayParty(call) {
    const type = classifyDirection(call);

    // If outgoing → show the person you're calling
    if (type === 'Outgoing' || type === 'Outbound External') {
      return callTo(call);
    }

    // Otherwise incoming → show the caller
    return callFrom(call);
  }

  function displayCallTarget(call) {
    const rgName = call.ring_group_name;
    const type = classifyDirection(call);

    // BUG-022: caller who dialed a ring group extension sees 'Direct' on their own row
    if (rgName && (type === 'Outgoing' || type === 'Outbound External')) {
      return isMobile ? '' : 'Direct';
    }

    if (rgName) {
      return displayWithRingGroupIcon(rgName, call.ring_group_extension, true);
    }

    if (isMobile) {
      return '';
    }

    return 'Direct';
  }

  function getCallConfirmDialogtext() {
    const { label, targetNumber } = confirmCall;

    if (label === targetNumber) {
      return `Call ${formatPhone(targetNumber)}?`;
    } else {
      return `Call ${label} at ${formatPhone(targetNumber)}?`;
    }
  }

  // ------------------ Pagination Controls ------------------

  const renderPaginationControls = () => {
    const previousCaretClass = isPreviousDisabled ? 'pagination__caret--disabled' : '';
    const nextCaretClass = isNextDisabled ? 'pagination__caret--disabled' : '';
    return (
      <div className="u-text-right u-text-small cdr-records__pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
        <span>Rows:</span>
        <select value={displayPageSize} onChange={handlePageSizeChange} style={{ padding: '2px 4px', fontSize: '13px' }}>
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
        <span className="inbox-pagination__count">{`${pageItemIndexFrom}-${pageItemIndexTo} of ${totalFilteredCount}`}</span>
        <Button
          type="link"
          size="small"
          iconOnly
          disabled={isPreviousDisabled}
          onClick={() => handlePagination('previous')}
          className={previousCaretClass}
        >
          <Icon icon="caret-left" />
        </Button>
        <Button
          type="link"
          iconOnly
          size="small"
          disabled={isNextDisabled}
          onClick={() => handlePagination('next')}
          className={nextCaretClass}
        >
          <Icon icon="caret-right" />
        </Button>
      </div>
    );
  };

  // ------------------ Render ------------------

  return (
    <>
      <div className="cdr-container">

        {/* Filter Bar */}
        <div className="cdr-records__filter-wrapper">
          <div className="cdr-records__filter-label">
            FILTER BY:
            {isAnyFilterSelected && (
              <Button type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
            )}
          </div>
          <div className="row">
            <div>
              <UtilityInlineGrid>
                <Dropdown
                  key={`direction-${filterResetKey}-${closeCounters.direction}`}
                  position="bottom"
                  activeKey={selectedDirections.length === 1 ? selectedDirections[0] : ''}
                  label={directionLabel}
                  type={isDirectionSelected ? 'primary' : 'input'}
                  outlined={!!isDirectionSelected}
                  name="recentDirection"
                  onClick={() => handleDropdownOpen('direction')}
                  onSelect={toggleDirection}
                  manualClose
                >
                  {DIRECTION_OPTIONS.map((item) => (
                    <DropdownMenuItem
                      key={item.id}
                      id={item.id}
                      label={item.value}
                      icon={selectedDirections.includes(item.id) ? 'checkmark' : ''}
                    />
                  ))}
                </Dropdown>
                <Dropdown
                  key={`status-${filterResetKey}-${closeCounters.status}`}
                  position="bottom"
                  activeKey={selectedStatuses.length === 1 ? selectedStatuses[0] : ''}
                  label={statusLabel}
                  type={isStatusSelected ? 'primary' : 'input'}
                  outlined={!!isStatusSelected}
                  name="recentStatus"
                  onClick={() => handleDropdownOpen('status')}
                  onSelect={toggleStatus}
                  manualClose
                >
                  {STATUS_OPTIONS.map((item) => (
                    <DropdownMenuItem
                      key={item.id}
                      id={item.id}
                      label={item.value}
                      icon={selectedStatuses.includes(item.id) ? 'checkmark' : ''}
                    />
                  ))}
                </Dropdown>
                <Dropdown
                  key={`member-${filterResetKey}-${closeCounters.member}`}
                  position="bottom"
                  activeKey={selectedMembers.length === 1 ? selectedMembers[0] : ''}
                  label={memberLabel}
                  type={isMemberSelected ? 'primary' : 'input'}
                  outlined={!!isMemberSelected}
                  name="recentMember"
                  onClick={() => handleDropdownOpen('member')}
                  onSelect={toggleMember}
                  manualClose
                >
                  <DropdownMenuItemWild>
                    <input
                      type="text"
                      className="form__control"
                      placeholder="Search..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DropdownMenuItemWild>
                  {memberOptions
                    .filter((item) => !memberSearch || item.value.toLowerCase().includes(memberSearch.toLowerCase()))
                    .map((item) => (
                      <DropdownMenuItem
                        key={item.id}
                        id={item.id}
                        label={item.value}
                        icon={selectedMembers.includes(item.id) ? 'checkmark' : ''}
                      />
                    ))}
                </Dropdown>
                <Dropdown
                  key={`contact-${filterResetKey}-${closeCounters.contact}`}
                  position="bottom"
                  activeKey={selectedContacts.length === 1 ? selectedContacts[0] : ''}
                  label={contactLabel}
                  type={isContactSelected ? 'primary' : 'input'}
                  outlined={!!isContactSelected}
                  name="recentContact"
                  onClick={() => handleDropdownOpen('contact')}
                  onSelect={toggleContact}
                  manualClose
                >
                  <DropdownMenuItemWild>
                    <input
                      type="text"
                      className="form__control"
                      placeholder="Search..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DropdownMenuItemWild>
                  {contactOptions
                    .filter((item) => !contactSearch || item.value.toLowerCase().includes(contactSearch.toLowerCase()))
                    .map((item) => (
                      <DropdownMenuItem
                        key={item.id}
                        id={item.id}
                        label={item.value}
                        icon={selectedContacts.includes(item.id) ? 'checkmark' : ''}
                      />
                    ))}
                </Dropdown>
                {includeRinggroups && (
                <Dropdown
                  key={`ringGroup-${filterResetKey}-${closeCounters.ringGroup}`}
                  position="bottom"
                  activeKey={selectedRingGroups.length === 1 ? selectedRingGroups[0] : ''}
                  label={ringGroupLabel}
                  type={isRingGroupSelected ? 'primary' : 'input'}
                  outlined={!!isRingGroupSelected}
                  name="recentRingGroup"
                  onClick={() => handleDropdownOpen('ringGroup')}
                  onSelect={toggleRingGroup}
                  manualClose
                >
                  <DropdownMenuItemWild>
                    <input
                      type="text"
                      className="form__control"
                      placeholder="Search..."
                      value={ringGroupSearch}
                      onChange={(e) => setRingGroupSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DropdownMenuItemWild>
                  {ringGroupOptions
                    .filter((item) => !ringGroupSearch || item.value.toLowerCase().includes(ringGroupSearch.toLowerCase()))
                    .map((item) => (
                      <DropdownMenuItem
                        key={item.id}
                        id={item.id}
                        label={item.value}
                        icon={selectedRingGroups.includes(item.id) ? 'checkmark' : ''}
                      />
                    ))}
                </Dropdown>
                )}
                <span className="recent-calls__ringgroup-toggle">
                  <RhinoSwitch
                    isChecked={includeRinggroups}
                    onChange={() => {
                      setIncludeRinggroups((prev) => {
                        if (prev) {
                          setSelectedRingGroups([]);
                          setRingGroupSearch('');
                        }
                        return !prev;
                      });
                      setPageNumber(0);
                    }}
                    name="includeRinggroups"
                  />
                  Include Ring Groups
                </span>
              </UtilityInlineGrid>
            </div>
          </div>
        </div>

        {paginatedCalls.length === 0 && allCalls.length === 0 && (
          <EmptyMessage section="Recent Calls" />
        )}
        {paginatedCalls.length === 0 && allCalls.length > 0 && (
          <div className="cdr-empty-state">No calls match the current filters.</div>
        )}

        {paginatedCalls.length > 0 && isMobile && (
        // -------- MOBILE VIEW --------
          <div className="cdr-mobile-list">
            {paginatedCalls.map((call) => {
              const type = classifyDirection(call);
              const icon = directionIcon(type);

              return (
                <div key={call.xml_cdr_uuid} className="cdr-mobile-card" onClick={() => handleMobileClick(call)}>
                  <div className="cdr-header-row">
                    <span className="cdr-name"><img src={icon} alt="direction" className="direction-icon" />&nbsp; &nbsp;{displayParty(call)} <br /> {displayCallTarget(call)}</span>
                    <span className="cdr-ts">{formatDate(call.start_stamp)}</span>
                  </div>

                  <div className="cdr-meta">
                    {` ${formatStatus(call.status) || 'n/a'} • ${formatDuration(call.duration)}`}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {paginatedCalls.length > 0 && !isMobile && (
        // -------- DESKTOP TABLE VIEW --------
          <div className="cdr-table-container">
            <table className="cdr-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Direct/Group</th>
                  <th>Status</th>
                  <th>Date/Time</th>
                  <th>Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedCalls.map((call) => {
                  const type = classifyDirection(call);
                  const icon = directionIcon(type);
                  const statusLower = call.status?.toLowerCase();
                  const isInbound = type === 'Incoming' || type === 'Inbound External';
                  const redRow = statusLower === 'missed'
                    || statusLower === 'no_answer'
                    || (isInbound && statusLower === 'voicemail');
                  return (
                    <tr key={call.xml_cdr_uuid} className={redRow ? 'cdr-row-missed' : ''}>
                      <td>
                        <img src={icon} alt="direction" className="direction-icon" title={type} />&nbsp;&nbsp;
                        {displayParty(call)}
                      </td>
                      <td>
                        {displayCallTarget(call)}
                      </td>
                      <td>{formatStatus(call.status)}</td>
                      <td>{formatDate(call.start_stamp)}</td>
                      <td>{formatDuration(call.duration)}</td>
                      <td>
                        <button type="button" className="cdr-action-icon" onClick={() => openCallConfirm(call)} title="Call">
                          <img src={makeCallIcon} alt="Call" width="24" height="24" />
                        </button>
                        {(call.caller_contact_name || call.destination_contact_name) && (
                          <button type="button" className="cdr-action-icon" onClick={(e) => { e.stopPropagation(); setSidePanelPhone(call.caller_contact_name ? call.caller_id_number : call.destination_number); }} title="Contact">
                            <img src={contactCardIcon} alt="Contact" width="24" height="24" />
                          </button>
                        )}
                        {call.voicemail_message_uuid && (
                          <button
                            type="button"
                            className="cdr-action-icon"
                            onClick={() => onNavigateToVoicemail(call.voicemail_message_uuid)}
                            title="Voicemail"
                          >
                            <img src={voicemailIcon} alt="Voicemail" width="24" height="24" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalFilteredCount > 0 && renderPaginationControls()}
        {confirmCall && ReactDOM.createPortal(
          <div className="cdr-modal-overlay">
            <div className="cdr-modal">
              <div className="cdr-modal-title">Confirm Call</div>
              <div className="cdr-modal-body">
                {getCallConfirmDialogtext(confirmCall)}
              </div>

              <div className="cdr-modal-actions">
                <button
                  type="button"
                  className="cdr-modal-cancel"
                  onClick={() => setConfirmCall(null)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="cdr-modal-confirm"
                  onClick={() => {
                    placeCall(confirmCall.targetNumber);
                    setConfirmCall(null);
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </div>
      <ContactSidePanel phoneNumber={sidePanelPhone} onClose={() => setSidePanelPhone(null)} />
    </>
  );
};

export default RhinoVOIPRecentCalls;
